import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "../fonts/breuer-headline.css";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import Swal from "sweetalert2";
import logoImg from "../images/logo.png";
// import playImg from "../images/play.png";

/** Truthy placeholder so `/stage2/broom` passes the wand gate when resuming from DB (no wand in session). */
const RESUME_WAND_PLACEHOLDER = "__hp_resume__";
const hasValidLobby = (value) => {
  if (value == null) return false;
  const normalized = String(value).trim().toLowerCase();
  return normalized !== "" && normalized !== "null" && normalized !== "undefined";
};

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status, user, error } = useSelector((state) => state.auth);
  const [reportChecking, setReportChecking] = useState(false);
  const resumeRouteDoneRef = useRef(false);
  const fromMobileAppFromQuery = (() => {
    const raw = new URLSearchParams(window.location.search).get("fromMobileApp");
    const normalized = String(raw || "").trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  })();
  const lobbyFromQuery = String(new URLSearchParams(window.location.search).get("lobby") || "").trim();
  const hasLobbyFromQuery = lobbyFromQuery.length > 0;
  const hasLobbyParam = hasLobbyFromQuery || sessionStorage.getItem("hasLobbyParam") === "true";
  const hasLobbyInUserData = hasValidLobby(user?.lobby);
  const beginPlayPath = hasLobbyInUserData ? "/game" : "/lobby";

  useEffect(() => {
    if (fromMobileAppFromQuery) {
      sessionStorage.setItem("fromMobileApp", "true");
    } else {
      sessionStorage.removeItem("fromMobileApp");
    }
  }, [fromMobileAppFromQuery]);

  useEffect(() => {
    if (hasLobbyFromQuery) {
      sessionStorage.setItem("hasLobbyParam", "true");
    } else {
      sessionStorage.removeItem("hasLobbyParam");
    }
  }, [hasLobbyFromQuery]);

  useEffect(() => {
    document.documentElement.classList.add("login-static-bg");
    document.body.classList.add("login-static-bg");
    return () => {
      document.documentElement.classList.remove("login-static-bg");
      document.body.classList.remove("login-static-bg");
    };
  }, []);

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect =
      user && user.backButtonRedirect ? user.backButtonRedirect : "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  useEffect(() => {
    if (status !== "succeeded" || !user) return;

    const isDemo =
      user.source === "DEMO" ||
      String(user.role || user.user_type || "").toLowerCase() === "demobypass";
    if (isDemo) return;

    const uid = user.userId || user.userid || user.id;
    const sid = user.sessionId;
    const oid = user.organizationId;
    if (!uid || !sid || !oid || !adminToken) return;
    if (resumeRouteDoneRef.current) return;

    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (!backendBase) return;

    let cancelled = false;
    setReportChecking(true);

    (async () => {
      try {
        const persistReportId = (rid) => {
          if (rid == null || String(rid).trim() === "") return;
          const normalized = String(rid).trim();
          try {
            const raw =
              localStorage.getItem("userData") ||
              sessionStorage.getItem("userData") ||
              "{}";
            const parsed = JSON.parse(raw) || {};
            if (String(parsed.reportId || "").trim() === normalized) return;
            const next = { ...parsed, reportId: normalized };
            localStorage.setItem("userData", JSON.stringify(next));
            sessionStorage.setItem("userData", JSON.stringify(next));
          } catch {
            // ignore storage issues
          }
        };

        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: String(uid),
            sessionId: String(sid),
            organizationId: String(oid),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || resumeRouteDoneRef.current) return;

        const back = user.backButtonRedirect || "/";
        const rpt = data.success ? data.report : null;
        const roleForApi =
          user.user_type ||
          user.userType ||
          user.role ||
          (user.source === "DEMO" ? "demobypass" : null) ||
          user.source ||
          "ORG_USER";
        const playerName =
          (typeof user.name === "string" && user.name.trim()) ||
          [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
          "";
        const externalRid =
          rpt?.reportId != null && String(rpt.reportId).trim() !== ""
            ? String(rpt.reportId).trim()
            : "";
        if (externalRid) {
          persistReportId(externalRid);
        } else {
          try {
            const seeded = await sendReport({
              sessionId: String(sid),
              organizationId: String(oid),
              role: roleForApi,
              token: user.token || "",
              points: rpt?.points != null ? Number(rpt.points) || 0 : 0,
              time:
                rpt?.time != null && String(rpt.time).trim() !== ""
                  ? String(rpt.time).trim()
                  : "00:00",
              gameId: user.gameId,
              name: playerName,
              userId: String(uid),
            });
            const seededRid = extractReportIdFromResponse(seeded);
            if (seededRid) {
              persistReportId(seededRid);
            }
          } catch (seedErr) {
            console.error("login seed sendReport:", seedErr);
          }
        }

        if (rpt && Number(rpt.gameover) === 1) {
          resumeRouteDoneRef.current = true;
          await Swal.fire("You have already played!", " ", "error");
          window.location.href = back;
          return;
        }

        if (rpt && Number(rpt.gameover) === 1) {
          resumeRouteDoneRef.current = true;
          const s1 = rpt.stage1 != null ? Number(rpt.stage1) : 0;
          navigate("/stage2/broom", {
            replace: true,
            state: {
              wandId: RESUME_WAND_PLACEHOLDER,
              points: Number.isFinite(s1) ? s1 : 0,
              time: rpt.time != null ? String(rpt.time) : "",
              reportId: rpt.reportId,
              stageId: rpt.id != null ? rpt.id : undefined,
            },
          });
          return;
        }

        if (rpt && Number(rpt.stage2) > 0 && Number(rpt.gameover) !== 2) {
          resumeRouteDoneRef.current = true;
          const s1 = rpt.stage1 != null ? Number(rpt.stage1) : NaN;
          const s2 = rpt.stage2 != null ? Number(rpt.stage2) : NaN;
          const pts = Number(rpt.points) || (Number.isFinite(s1) && Number.isFinite(s2) ? s1 + s2 : 0);
          navigate("/thankyou", {
            replace: true,
            state: {
              points: pts,
              time: rpt.time != null ? String(rpt.time) : "",
              stageId: rpt.id != null ? rpt.id : undefined,
              spellScore: Number.isFinite(s1) ? s1 : pts - (Number.isFinite(s2) ? s2 : 0),
              flightScore: Number.isFinite(s2) ? s2 : 0,
            },
          });
          return;
        }

        // No report for this user+session+org: allow play. Do not use login API userPlayedCount
        // (cumulative / stale vs localStorage) — "already played" is only from fetchReport above.
      } catch (e) {
        console.error("login fetchReport:", e);
      } finally {
        if (!cancelled) setReportChecking(false);
      }
    })();

    return () => {
      cancelled = true;
      setReportChecking(false);
    };
  }, [status, user, adminToken, navigate]);

  if (status === "loading" || (status === "succeeded" && user && reportChecking)) {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      <img src={logoImg} className="login-logo1" alt="Game Logo" />
      {user && status === "succeeded" && (
        <div>
          <Link
            to={beginPlayPath}
            className="begin-play-btn"
          >
            <button  className="begin-play-img" alt="Begin play" >Begin play</button>
          </Link>
        </div>
      )}
    </div>
  );
}

export default Login;
